import type {
  BinaryOperator,
  CaseComparisonOperator,
  CaseRange,
  Expression,
  IfStatement,
  LValue,
  NumberLiteral,
  Statement,
  SwitchCase,
  SwitchCaseValue,
  SwitchStatement,
} from '../parser/ast.js';

/**
 * Μετατροπές δομών επιλογής (βλ. σχολικό ΑΕΠΠ): Απλή/Σύνθετη/Πολλαπλή ΑΝ,
 * ΕΠΙΛΕΞΕ, και οι δύο μορφές εμφωλευμένου ΑΝ (μέσα σε ΑΛΛΙΩΣ ≡ αλυσίδα
 * ΑΛΛΙΩΣ_ΑΝ· μέσα σε ΤΟΤΕ ≡ σύνθετη συνθήκη με ΚΑΙ).
 *
 * Κάθε συνάρτηση `xToY` μετατρέπει ΕΝΑ κόμβο σε ΕΝΑ ισοδύναμο κόμβο, εκτός
 * από το `toSimpleSequence` (Σύνθετη/Πολλαπλή → Απλή) που τον σπάει σε Ν
 * ανεξάρτητες Απλές ΑΝ — και το αντίστροφό του `findSimpleChains`/
 * `mergeSimpleChain`, που αναγνωρίζει Ν διαδοχικές Απλές ΑΝ και τις ενώνει
 * ξανά. Καμία μετατροπή δεν χάνει πληροφορία. Όταν μια μετατροπή δεν είναι
 * δομικά έγκυρη για τον συγκεκριμένο κόμβο (π.χ. ΑΝ με ανομοιογενείς
 * συνθήκες → ΕΠΙΛΕΞΕ), η συνάρτηση επιστρέφει `null` αντί να παράγει
 * λάθος/παραπλανητικό αποτέλεσμα.
 */

export type SelectionShape = 'simple' | 'composite' | 'multiple' | 'nested-else' | 'nested-then' | 'switch';

/** Ό,τι χρειάζεται μόνο τη θέση (γραμμή/στήλη) ενός κόμβου — π.χ. για να "δανειστεί" τη θέση ενός IfStatement/ForStatement/WhileStatement όταν χτίζουμε μια νέα, συνθετική έκφραση. */
export interface NodePosition {
  readonly line: number;
  readonly column: number;
}

// --- Βοηθητικά: ισότητα/άρνηση εκφράσεων ------------------------------------

function exprEquals(a: Expression, b: Expression): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'NumberLiteral':
      return a.value === (b as typeof a).value;
    case 'StringLiteral':
      return a.value === (b as typeof a).value;
    case 'BooleanLiteral':
      return a.value === (b as typeof a).value;
    case 'Identifier':
      return a.name === (b as typeof a).name;
    case 'ArrayAccess': {
      const bb = b as typeof a;
      return (
        a.array.name === bb.array.name &&
        a.indices.length === bb.indices.length &&
        a.indices.every((idx, i) => exprEquals(idx, bb.indices[i]!))
      );
    }
    case 'FunctionCall': {
      const bb = b as typeof a;
      return (
        a.callee.name === bb.callee.name &&
        a.args.length === bb.args.length &&
        a.args.every((arg, i) => exprEquals(arg, bb.args[i]!))
      );
    }
    case 'UnaryExpression': {
      const bb = b as typeof a;
      return a.operator === bb.operator && exprEquals(a.operand, bb.operand);
    }
    case 'BinaryExpression': {
      const bb = b as typeof a;
      return a.operator === bb.operator && exprEquals(a.left, bb.left) && exprEquals(a.right, bb.right);
    }
  }
}

export function negateComparisonOperator(op: BinaryOperator): BinaryOperator | null {
  switch (op) {
    case '=':
      return '<>';
    case '<>':
      return '=';
    case '<':
      return '>=';
    case '>=':
      return '<';
    case '>':
      return '<=';
    case '<=':
      return '>';
    default:
      return null;
  }
}

/** true αν `b` είναι η λογική άρνηση του `a` — ρητά (ΟΧΙ a), ως αντίστροφος συγκριτικός τελεστής στα ίδια μέλη (x>5 ↔ x<=5), ως αντίστροφη λογική τιμή (ΑΛΗΘΗΣ ↔ ΨΕΥΔΗΣ), ή αναδρομικά μέσω De Morgan (α ΚΑΙ β ↔ ¬α Η ¬β). */
export function isDirectNegation(a: Expression, b: Expression): boolean {
  if (b.kind === 'UnaryExpression' && b.operator === 'ΟΧΙ' && exprEquals(b.operand, a)) return true;
  if (a.kind === 'UnaryExpression' && a.operator === 'ΟΧΙ' && exprEquals(a.operand, b)) return true;
  if (a.kind === 'BooleanLiteral' && b.kind === 'BooleanLiteral') return a.value !== b.value;
  if (a.kind === 'BinaryExpression' && b.kind === 'BinaryExpression') {
    const negated = negateComparisonOperator(a.operator);
    if (negated !== null && negated === b.operator && exprEquals(a.left, b.left) && exprEquals(a.right, b.right)) {
      return true;
    }
    if (a.operator === 'ΚΑΙ' && b.operator === 'Η' && isDirectNegation(a.left, b.left) && isDirectNegation(a.right, b.right)) {
      return true;
    }
    if (a.operator === 'Η' && b.operator === 'ΚΑΙ' && isDirectNegation(a.left, b.left) && isDirectNegation(a.right, b.right)) {
      return true;
    }
  }
  return false;
}

/**
 * Λογική άρνηση μιας συνθήκης ΧΩΡΙΣ τον τελεστή ΟΧΙ, όποτε είναι δυνατό: για
 * συγκριτικούς τελεστές χρησιμοποιεί τον αντίστροφο (π.χ. `>` → `<=`), για
 * ΚΑΙ/Η εφαρμόζει De Morgan αναδρομικά, για λογική τιμή αντιστρέφει
 * ΑΛΗΘΗΣ/ΨΕΥΔΗΣ, και ακυρώνει ένα ήδη υπάρχον ΟΧΙ. Μόνο όταν καμία από αυτές
 * τις περιπτώσεις δεν ισχύει (π.χ. λογική μεταβλητή ή κλήση συνάρτησης χωρίς
 * τελεστή προς αναστροφή) καταφεύγει στο ΟΧΙ, ως μόνη δυνατή επιλογή.
 */
export function negateCondition(expr: Expression, pos: NodePosition): Expression {
  if (expr.kind === 'BooleanLiteral') {
    return { kind: 'BooleanLiteral', value: !expr.value, line: pos.line, column: pos.column };
  }
  if (expr.kind === 'UnaryExpression' && expr.operator === 'ΟΧΙ') {
    return expr.operand;
  }
  if (expr.kind === 'BinaryExpression') {
    const flipped = negateComparisonOperator(expr.operator);
    if (flipped !== null) {
      return { kind: 'BinaryExpression', operator: flipped, left: expr.left, right: expr.right, line: pos.line, column: pos.column };
    }
    if (expr.operator === 'ΚΑΙ' || expr.operator === 'Η') {
      return {
        kind: 'BinaryExpression',
        operator: expr.operator === 'ΚΑΙ' ? 'Η' : 'ΚΑΙ',
        left: negateCondition(expr.left, pos),
        right: negateCondition(expr.right, pos),
        line: pos.line,
        column: pos.column,
      };
    }
  }
  return notExpr(expr, pos);
}

// --- Ταξινόμηση --------------------------------------------------------------

function isNestedElse(stmt: IfStatement): boolean {
  return (
    stmt.elseIfBranches.length === 0 &&
    stmt.elseBranch !== null &&
    stmt.elseBranch.length === 1 &&
    stmt.elseBranch[0]!.kind === 'IfStatement'
  );
}

function isNestedThen(stmt: IfStatement): boolean {
  if (stmt.elseIfBranches.length > 0 || stmt.elseBranch !== null) return false;
  if (stmt.thenBranch.length !== 1) return false;
  const inner = stmt.thenBranch[0]!;
  return inner.kind === 'IfStatement' && inner.elseIfBranches.length === 0 && inner.elseBranch === null;
}

export function classifyIf(stmt: IfStatement): Exclude<SelectionShape, 'switch'> {
  if (stmt.elseIfBranches.length > 0) return 'multiple';
  if (isNestedElse(stmt)) return 'nested-else';
  if (isNestedThen(stmt)) return 'nested-then';
  if (stmt.elseBranch !== null) return 'composite';
  return 'simple';
}

// --- Πολλαπλή ↔ ΕΠΙΛΕΞΕ -------------------------------------------------------

const COMPARISON_OPERATORS: readonly BinaryOperator[] = ['<', '>', '<=', '>=', '<>'];

function isComparisonOperator(op: BinaryOperator): op is CaseComparisonOperator {
  return (COMPARISON_OPERATORS as readonly string[]).includes(op);
}

/** Ψάχνει το αριστερό μέλος της πρώτης απλής σύγκρισης μέσα σε μια (πιθανώς Η/ΚΑΙ-αλυσίδα, π.χ. εύρος "x>=2 ΚΑΙ x<=100") συνθήκη — υποψήφιο "discriminant" για ΕΠΙΛΕΞΕ. */
function findFirstComparisonLeft(condition: Expression): Expression | null {
  if (condition.kind !== 'BinaryExpression') return null;
  if (condition.operator === 'Η' || condition.operator === 'ΚΑΙ') {
    return findFirstComparisonLeft(condition.left) ?? findFirstComparisonLeft(condition.right);
  }
  if (condition.operator === '=' || isComparisonOperator(condition.operator)) return condition.left;
  return null;
}

// --- Αλγεβρική αναγνώριση discriminant (π.χ. "Κ = -Λ" ↔ "Κ+Λ = 0") --------

/** Ένας «ατομικός» όρος (οτιδήποτε ΔΕΝ είναι το ίδιο +/- σε επίπεδο κορυφής) μέσα σε ένα ισοπεδωμένο άθροισμα, με το πρόσημό του. */
interface SignedTerm {
  readonly sign: 1 | -1;
  readonly expr: Expression;
}

/** Ισοπεδώνει μια έκφραση χτισμένη ΑΠΟΚΛΕΙΣΤΙΚΑ από +, -, μοναδιαίο - σε λίστα προσημασμένων ατομικών όρων + αριθμητική σταθερά (π.χ. "Κ+Λ-3" → όροι [+Κ,+Λ], σταθερά -3). Οτιδήποτε άλλο (πολλαπλασιασμός, μεταβλητή, κλήση συνάρτησης) γίνεται ΕΝΑΣ ατομικός όρος, χωρίς περαιτέρω ανάλυση — σκόπιμα ΔΕΝ υποστηρίζονται συντελεστές (π.χ. "2*Κ" δεν ταυτίζεται με "Κ"). */
function flattenAdditive(expr: Expression, sign: 1 | -1): { terms: SignedTerm[]; constant: number } {
  if (expr.kind === 'BinaryExpression' && expr.operator === '+') {
    const l = flattenAdditive(expr.left, sign);
    const r = flattenAdditive(expr.right, sign);
    return { terms: [...l.terms, ...r.terms], constant: l.constant + r.constant };
  }
  if (expr.kind === 'BinaryExpression' && expr.operator === '-') {
    const l = flattenAdditive(expr.left, sign);
    const r = flattenAdditive(expr.right, (sign * -1) as 1 | -1);
    return { terms: [...l.terms, ...r.terms], constant: l.constant + r.constant };
  }
  if (expr.kind === 'UnaryExpression' && expr.operator === '-') {
    return flattenAdditive(expr.operand, (sign * -1) as 1 | -1);
  }
  if (expr.kind === 'NumberLiteral') {
    return { terms: [], constant: sign * expr.value };
  }
  return { terms: [{ sign, expr }], constant: 0 };
}

/** Απλοποιεί μια λίστα προσημασμένων όρων, ακυρώνοντας ζεύγη του ΙΔΙΟΥ atom με αντίθετο πρόσημο (π.χ. +Κ και -Κ αλληλοαναιρούνται σε "Κ+Λ-Κ" → μόνο +Λ). `null` αν κάποιο atom απομένει με πρόσημο εκτός ±1 (π.χ. "Κ+Κ" = 2Κ — συντελεστής που δεν υποστηρίζουμε). */
function cancelTerms(terms: readonly SignedTerm[]): SignedTerm[] | null {
  const used = new Array<boolean>(terms.length).fill(false);
  const result: SignedTerm[] = [];
  for (let i = 0; i < terms.length; i++) {
    if (used[i]) continue;
    used[i] = true;
    let total = terms[i]!.sign as number;
    for (let j = i + 1; j < terms.length; j++) {
      if (used[j] || !exprEquals(terms[j]!.expr, terms[i]!.expr)) continue;
      used[j] = true;
      total += terms[j]!.sign;
    }
    if (total === 0) continue;
    if (total !== 1 && total !== -1) return null;
    result.push({ sign: total, expr: terms[i]!.expr });
  }
  return result;
}

/** true αν οι δύο λίστες κανονικοποιημένων όρων αντιπροσωπεύουν το ΙΔΙΟ άθροισμα (ή, με `negateB`, το ακριβώς αντίθετο) — ανεξαρτήτως σειράς. */
function termsMatch(a: readonly SignedTerm[], b: readonly SignedTerm[], negateB: boolean): boolean {
  if (a.length !== b.length) return false;
  const usedB = new Array<boolean>(b.length).fill(false);
  for (const ta of a) {
    const found = b.findIndex((tb, j) => {
      if (usedB[j]) return false;
      const bSign = negateB ? (-tb.sign as 1 | -1) : tb.sign;
      return bSign === ta.sign && exprEquals(ta.expr, tb.expr);
    });
    if (found === -1) return false;
    usedB[found] = true;
  }
  return true;
}

function numberLiteral(value: number, pos: NodePosition): NumberLiteral {
  return { kind: 'NumberLiteral', value, line: pos.line, column: pos.column };
}

/** "Ρυθμίζει" τον τελεστή μιας σύγκρισης όταν πολλαπλασιάζουμε ΚΑΙ τα δύο μέλη με -1 (π.χ. "-Α < -Β" ⟺ "Α > Β") — ΔΙΑΦΟΡΕΤΙΚΟ από τη λογική άρνηση (`negateComparisonOperator`): εδώ η ΑΛΗΘΕΙΑ της σύγκρισης διατηρείται, μόνο η φορά αντιστρέφεται. */
function mirrorComparisonOperator(op: BinaryOperator): BinaryOperator | null {
  switch (op) {
    case '=':
      return '=';
    case '<>':
      return '<>';
    case '<':
      return '>';
    case '>':
      return '<';
    case '<=':
      return '>=';
    case '>=':
      return '<=';
    default:
      return null;
  }
}

/**
 * Προσπαθεί να ξαναγράψει μια σύγκριση `left op right` ως `discriminant op' value`
 * μέσω απλής άλγεβρας (μετακίνηση όρων), για τις περιπτώσεις που η άμεση σύγκριση
 * (`exprEquals(left, discriminant)`) αποτυγχάνει — π.χ. "Κ = -Λ" ↔ "Κ+Λ = 0" όταν
 * το discriminant είναι "Κ+Λ". Υποστηρίζει ΜΟΝΟ εκφράσεις χτισμένες από +, -,
 * μοναδιαίο - (βλ. `flattenAdditive`) — σκόπιμα συντηρητικό, `null` όποτε δεν
 * μπορεί να αποδείξει με σιγουριά την ισοδυναμία (καμία μαντεψιά/προσέγγιση).
 */
function rewriteComparisonToDiscriminant(
  operator: BinaryOperator,
  left: Expression,
  right: Expression,
  discriminant: Expression,
): { operator: BinaryOperator; value: Expression } | null {
  const lFlat = flattenAdditive(left, 1);
  const rFlat = flattenAdditive(right, -1);
  const combinedTerms = cancelTerms([...lFlat.terms, ...rFlat.terms]);
  if (combinedTerms === null) return null;
  const combinedConstant = lFlat.constant + rFlat.constant;

  const dFlat = flattenAdditive(discriminant, 1);
  const dTerms = cancelTerms(dFlat.terms);
  if (dTerms === null || dTerms.length === 0) return null;

  if (termsMatch(combinedTerms, dTerms, false)) {
    return { operator, value: numberLiteral(dFlat.constant - combinedConstant, discriminant) };
  }
  if (termsMatch(combinedTerms, dTerms, true)) {
    const mirrored = mirrorComparisonOperator(operator);
    if (mirrored === null) return null;
    return { operator: mirrored, value: numberLiteral(dFlat.constant + combinedConstant, discriminant) };
  }
  return null;
}

/** Αν η σύγκριση (άμεσα ή μέσω `rewriteComparisonToDiscriminant`) ισοδυναμεί με "discriminant >= τιμή" ή "discriminant <= τιμή", επιστρέφει το είδος ορίου + την τιμή — ΑΚΡΙΒΩΣ οι τελεστές που παράγει η αντίστροφη μετατροπή ΕΠΙΛΕΞΕ→ΑΝ για ένα CaseRange (βλ. `switchCaseValueToCondition`), ώστε η αναγνώριση να είναι πάντα ισοδύναμη/μη-παραπλανητική (ΔΕΝ δέχεται γνήσια > ή < — θα άλλαζε τη σημασιολογία του κλειστού διαστήματος). */
function matchRangeBound(condition: Expression, discriminant: Expression): { kind: 'lower' | 'upper'; value: Expression } | null {
  if (condition.kind !== 'BinaryExpression') return null;
  const rewritten = exprEquals(condition.left, discriminant)
    ? { operator: condition.operator, value: condition.right }
    : rewriteComparisonToDiscriminant(condition.operator, condition.left, condition.right, discriminant);
  if (rewritten === null) return null;
  if (rewritten.operator === '>=') return { kind: 'lower', value: rewritten.value };
  if (rewritten.operator === '<=') return { kind: 'upper', value: rewritten.value };
  return null;
}

/** Αναγνωρίζει "discriminant >= Χ ΚΑΙ discriminant <= Υ" (σε οποιαδήποτε σειρά) ως ΕΝΑ CaseRange Χ..Υ. `null` αν δεν ταιριάζει το μοτίβο ή αν λείπει ένα από τα δύο άκρα. */
function conditionToRange(condition: Expression, discriminant: Expression): CaseRange | null {
  if (condition.kind !== 'BinaryExpression' || condition.operator !== 'ΚΑΙ') return null;
  const leftBound = matchRangeBound(condition.left, discriminant);
  const rightBound = matchRangeBound(condition.right, discriminant);
  if (leftBound === null || rightBound === null) return null;

  let from: Expression | null = null;
  let to: Expression | null = null;
  for (const bound of [leftBound, rightBound]) {
    if (bound.kind === 'lower') from = bound.value;
    else to = bound.value;
  }
  if (from === null || to === null) return null;

  return { kind: 'CaseRange', from, to, line: condition.line, column: condition.column };
}

/** Μετατρέπει μια συνθήκη σε λίστα SwitchCaseValue, όταν αποτελείται αποκλειστικά από (discriminant = τιμή) ή (discriminant <συγκρ.τελεστής> τιμή) ενωμένα με Η, ή είναι ένα εύρος "discriminant >= Χ ΚΑΙ discriminant <= Υ". Η σύγκριση με το discriminant γίνεται πρώτα ΑΚΡΙΒΩΣ (exprEquals), και αν αποτύχει δοκιμάζεται αλγεβρική αναδιατύπωση (`rewriteComparisonToDiscriminant`) — π.χ. "Κ = -Λ" αναγνωρίζεται όταν discriminant είναι "Κ+Λ". `null` αν δεν ταιριάζει κανένα μοτίβο. */
function conditionToSwitchValues(condition: Expression, discriminant: Expression): SwitchCaseValue[] | null {
  if (condition.kind === 'BinaryExpression' && condition.operator === 'Η') {
    const left = conditionToSwitchValues(condition.left, discriminant);
    if (left === null) return null;
    const right = conditionToSwitchValues(condition.right, discriminant);
    if (right === null) return null;
    return [...left, ...right];
  }
  if (condition.kind === 'BinaryExpression' && condition.operator === 'ΚΑΙ') {
    const range = conditionToRange(condition, discriminant);
    return range === null ? null : [range];
  }
  if (condition.kind !== 'BinaryExpression') return null;
  if (condition.operator !== '=' && !isComparisonOperator(condition.operator)) return null;

  if (exprEquals(condition.left, discriminant)) {
    if (condition.operator === '=') return [condition.right];
    return [{ kind: 'CaseComparison', operator: condition.operator, value: condition.right, line: condition.line, column: condition.column }];
  }

  const rewritten = rewriteComparisonToDiscriminant(condition.operator, condition.left, condition.right, discriminant);
  if (rewritten === null) return null;
  if (rewritten.operator === '=') return [rewritten.value];
  if (isComparisonOperator(rewritten.operator)) {
    return [{ kind: 'CaseComparison', operator: rewritten.operator, value: rewritten.value, line: condition.line, column: condition.column }];
  }
  return null;
}

/**
 * Υποψήφια discriminants για ΕΠΙΛΕΞΕ: το αριστερό μέλος της πρώτης απλής
 * σύγκρισης ΚΑΘΕ κλάδου (κύριο + κάθε ΑΛΛΙΩΣ_ΑΝ), με προτεραιότητα στον
 * κύριο κλάδο (συνήθως το πιο φυσικό discriminant) — αλλά όχι τον ΜΟΝΑΔΙΚΟ
 * υποψήφιο: π.χ. σε "Αν Κ = -Λ ... Αλλιώς_αν Κ+Λ = 1 ..." ο κύριος κλάδος
 * δίνει discriminant "Κ", που δεν ταιριάζει στον 2ο κλάδο· δοκιμάζοντας ΚΑΙ
 * το "Κ+Λ" (discriminant υποψήφιο του 2ου κλάδου) βρίσκουμε ότι δουλεύει σε
 * ΟΛΟΥΣ τους κλάδους, αφού "Κ = -Λ" ξαναγράφεται αλγεβρικά σε "Κ+Λ = 0" (βλ.
 * `rewriteComparisonToDiscriminant`).
 */
function candidateDiscriminants(stmt: IfStatement): Expression[] {
  const candidates: Expression[] = [];
  const tryAdd = (expr: Expression | null): void => {
    if (expr !== null && !candidates.some((c) => exprEquals(c, expr))) candidates.push(expr);
  };
  tryAdd(findFirstComparisonLeft(stmt.condition));
  for (const branch of stmt.elseIfBranches) tryAdd(findFirstComparisonLeft(branch.condition));
  return candidates;
}

/** Πολλαπλή ΑΝ/ΑΛΛΙΩΣ_ΑΝ → ΕΠΙΛΕΞΕ — μόνο αν ΟΛΕΣ οι συνθήκες συγκρίνουν (άμεσα, αλγεβρικά, ή σαν εύρος με ΚΑΙ) την ίδια έκφραση με σταθερές/συγκριτικούς τελεστές. Δοκιμάζει πολλαπλά υποψήφια discriminants (βλ. `candidateDiscriminants`) πριν επιστρέψει `null`. */
export function multipleToSwitch(stmt: IfStatement): SwitchStatement | null {
  for (const discriminant of candidateDiscriminants(stmt)) {
    const mainValues = conditionToSwitchValues(stmt.condition, discriminant);
    if (mainValues === null) continue;
    const cases: SwitchCase[] = [{ values: mainValues, body: stmt.thenBranch }];

    let allBranchesMatch = true;
    for (const branch of stmt.elseIfBranches) {
      const values = conditionToSwitchValues(branch.condition, discriminant);
      if (values === null) {
        allBranchesMatch = false;
        break;
      }
      cases.push({ values, body: branch.body });
    }
    if (!allBranchesMatch) continue;

    return {
      kind: 'SwitchStatement',
      discriminant,
      cases,
      elseBody: stmt.elseBranch,
      endLine: null,
      line: stmt.line,
      column: stmt.column,
    };
  }
  return null;
}

function switchCaseValueToCondition(discriminant: Expression, value: SwitchCaseValue): Expression {
  if (value.kind === 'CaseRange') {
    return {
      kind: 'BinaryExpression',
      operator: 'ΚΑΙ',
      left: { kind: 'BinaryExpression', operator: '>=', left: discriminant, right: value.from, line: value.line, column: value.column },
      right: { kind: 'BinaryExpression', operator: '<=', left: discriminant, right: value.to, line: value.line, column: value.column },
      line: value.line,
      column: value.column,
    };
  }
  if (value.kind === 'CaseComparison') {
    return { kind: 'BinaryExpression', operator: value.operator, left: discriminant, right: value.value, line: value.line, column: value.column };
  }
  return { kind: 'BinaryExpression', operator: '=', left: discriminant, right: value, line: value.line, column: value.column };
}

function caseValuesToCondition(discriminant: Expression, values: readonly SwitchCaseValue[]): Expression {
  return values
    .map((v) => switchCaseValueToCondition(discriminant, v))
    .reduce((acc, cond) => (acc === null ? cond : { kind: 'BinaryExpression', operator: 'Η', left: acc, right: cond, line: cond.line, column: cond.column }));
}

/** ΕΠΙΛΕΞΕ → Πολλαπλή (ή Σύνθετη/Απλή αν έχει λίγες ΠΕΡΙΠΤΩΣΗ) ΑΝ/ΑΛΛΙΩΣ_ΑΝ — πάντα εφικτή όσο υπάρχει τουλάχιστον μία ΠΕΡΙΠΤΩΣΗ (όχι μόνο ΑΛΛΙΩΣ). */
export function switchToMultiple(stmt: SwitchStatement): IfStatement | null {
  const [first, ...rest] = stmt.cases;
  if (first === undefined) return null;
  return {
    kind: 'IfStatement',
    condition: caseValuesToCondition(stmt.discriminant, first.values),
    thenBranch: first.body,
    elseIfBranches: rest.map((c) => ({ condition: caseValuesToCondition(stmt.discriminant, c.values), body: c.body })),
    elseBranch: stmt.elseBody,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
}

// --- Πολλαπλή ↔ Εμφωλευμένο ΑΝ σε ΑΛΛΙΩΣ -------------------------------------

/** Επίπεδη αλυσίδα ΑΝ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ → βαθιά εμφωλευμένο ΑΝ...ΑΛΛΙΩΣ{ΑΝ...}. Πάντα εφικτή. */
export function multipleToNestedElse(stmt: IfStatement): IfStatement {
  if (stmt.elseIfBranches.length === 0) return stmt;
  const [firstBranch, ...restBranches] = stmt.elseIfBranches;
  const nested = multipleToNestedElse({
    kind: 'IfStatement',
    condition: firstBranch!.condition,
    thenBranch: firstBranch!.body,
    elseIfBranches: restBranches,
    elseBranch: stmt.elseBranch,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  });
  return {
    kind: 'IfStatement',
    condition: stmt.condition,
    thenBranch: stmt.thenBranch,
    elseIfBranches: [],
    elseBranch: [nested],
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
}

/** Βαθιά εμφωλευμένο ΑΝ...ΑΛΛΙΩΣ{ΑΝ...} → επίπεδη αλυσίδα ΑΝ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ. Απαιτεί `classifyIf(stmt) === 'nested-else'`. */
export function nestedElseToMultiple(stmt: IfStatement): IfStatement {
  if (!isNestedElse(stmt)) return stmt;
  const inner = stmt.elseBranch![0] as IfStatement;
  const flattenedInner = nestedElseToMultiple(inner);
  return {
    kind: 'IfStatement',
    condition: stmt.condition,
    thenBranch: stmt.thenBranch,
    elseIfBranches: [{ condition: flattenedInner.condition, body: flattenedInner.thenBranch }, ...flattenedInner.elseIfBranches],
    elseBranch: flattenedInner.elseBranch,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
}

// --- Πλήρες ξεδίπλωμα (οποιοδήποτε βάθος εμφωλευμένων ΑΝ) -------------------

/**
 * Το `isNestedElse`/`isNestedThen` πιο πάνω αναγνωρίζουν μόνο το ΣΤΕΝΟ σχήμα
 * "ο κλάδος περιέχει ΑΠΟΚΛΕΙΣΤΙΚΑ ένα εμφωλευμένο ΑΝ" — εδώ γενικεύουμε: ο
 * κλάδος μπορεί να έχει ΚΑΙ άλλες εντολές πριν/μετά το εμφωλευμένο ΑΝ (π.χ.
 * "Κ ← ...  /  Αν ...  /  ..." μέσα σε ΑΛΛΙΩΣ), κάτι πολύ πιο συνηθισμένο σε
 * πραγματικό κώδικα μαθητή. Ξεδιπλώνει ΟΠΟΙΟΔΗΠΟΤΕ βάθος εμφώλευσης σε ΜΙΑ
 * επίπεδη Πολλαπλή, επαναλαμβάνοντας τις "πριν/μετά" εντολές σε ΚΑΘΕ κλάδο
 * που προκύπτει από το αντίστοιχο υπο-δέντρο (ώστε να τρέχουν ακριβώς όπως
 * πριν) — ΧΩΡΙΣ καμία προσπάθεια εξάλειψης "νεκρών" αναθέσεων (μια ανάθεση
 * που αμέσως ξαναγράφεται σε κάποιο κλάδο μπορεί να μείνει, αβλαβώς
 * περιττή) — βλ. σχόλιο `flattenNestedIf`.
 */

function containsTopLevelIf(body: readonly Statement[]): boolean {
  return body.some((s) => s.kind === 'IfStatement');
}

/** true αν ΚΑΠΟΥ μέσα στο ΑΜΕΣΟ σώμα κάποιου κλάδου (ΤΟΤΕ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ) υπάρχει εμφωλευμένο ΑΝ — δεν κοιτάει μέσα σε βρόχους/ΕΠΙΛΕΞΕ (δεν τους αγγίζουμε). */
function ifHasAnyNesting(stmt: IfStatement): boolean {
  if (containsTopLevelIf(stmt.thenBranch)) return true;
  for (const branch of stmt.elseIfBranches) if (containsTopLevelIf(branch.body)) return true;
  if (stmt.elseBranch !== null && containsTopLevelIf(stmt.elseBranch)) return true;
  return false;
}

/** Συνδυάζει δύο (πιθανώς `null` = "χωρίς επιπλέον συνθήκη, απλό ΑΛΛΙΩΣ") συνθήκες με ΚΑΙ — `null` όποτε η μία πλευρά είναι ήδη `null`, αφού δεν χρειάζεται. */
function combineConditions(outer: Expression | null, inner: Expression | null): Expression | null {
  if (outer === null) return inner;
  if (inner === null) return outer;
  return andExpr(outer, inner, outer);
}

interface FlatLeaf {
  /** `null` = αυτός ο κλάδος δεν χρειάζεται (επιπλέον) συνθήκη — θα γίνει το τελικό ΑΛΛΙΩΣ. */
  readonly condition: Expression | null;
  readonly body: Statement[];
}

/**
 * Ξεδιπλώνει το σώμα ΕΝΟΣ κλάδου (π.χ. το ΤΟΤΕ, ή ένα ΑΛΛΙΩΣ_ΑΝ, ή το
 * ΑΛΛΙΩΣ) σε λίστα "φύλλων" — `prefixCondition` είναι η συνθήκη που ήδη
 * χρειάζεται για να φτάσει κανείς σε αυτό το σώμα. Δέχεται ΤΟ ΠΟΛΥ ένα
 * top-level ΑΝ μέσα στο σώμα (πριν/μετά αυτό επιτρέπονται άλλες εντολές) —
 * `null` αν υπάρχουν 2+ (πολυπλοκότητα εκτός του πεδίου αυτής της
 * μετατροπής, "decline don't guess"), ή αν η συνθήκη του εμφωλευμένου ΑΝ
 * εξαρτάται από μεταβλητή που ΑΛΛΑΖΕΙ σε κάποια εντολή ΠΡΙΝ από αυτό μέσα
 * στο ΙΔΙΟ σώμα (θα σήμαινε ότι η συνθήκη πρέπει να ελεγχθεί ΜΕΤΑ από αυτή
 * την αλλαγή — στην επίπεδη μορφή όμως η συνθήκη ελέγχεται ΠΡΙΝ τρέξει
 * τίποτα από το σώμα, οπότε θα έβλεπε την ΠΑΛΙΑ τιμή — μη ασφαλές).
 */
function flattenBranchBody(prefixCondition: Expression | null, body: readonly Statement[]): FlatLeaf[] | null {
  const ifCount = body.filter((s) => s.kind === 'IfStatement').length;
  if (ifCount === 0) return [{ condition: prefixCondition, body: [...body] }];
  if (ifCount > 1) return null;

  const idx = body.findIndex((s) => s.kind === 'IfStatement');
  const leading = body.slice(0, idx);
  const nested = body[idx] as IfStatement;
  const trailing = body.slice(idx + 1);

  if (setsIntersect(assignedNames(leading), referencedNames(nested.condition))) return null;

  const nestedLeaves = flattenIfToLeaves(nested);
  if (nestedLeaves === null) return null;

  return nestedLeaves.map((leaf) => ({
    condition: combineConditions(prefixCondition, leaf.condition),
    body: [...leading, ...leaf.body, ...trailing],
  }));
}

/** Ξεδιπλώνει ΟΛΟΚΛΗΡΟ ένα (πιθανώς ήδη Πολλαπλό) ΑΝ σε λίστα "φύλλων" — ΠΑΝΤΑ καταλήγει με ΑΚΡΙΒΩΣ ένα φύλλο `condition: null` (το τελικό ΑΛΛΙΩΣ — άδειο σώμα αν δεν υπήρχε ήδη ΑΛΛΙΩΣ, δηλαδή "δεν κάνε τίποτα"). */
function flattenIfToLeaves(stmt: IfStatement): FlatLeaf[] | null {
  const branches = [{ condition: stmt.condition, body: stmt.thenBranch }, ...stmt.elseIfBranches];
  const leaves: FlatLeaf[] = [];
  for (const branch of branches) {
    const sub = flattenBranchBody(branch.condition, branch.body);
    if (sub === null) return null;
    leaves.push(...sub);
  }
  const sub = flattenBranchBody(null, stmt.elseBranch ?? []);
  if (sub === null) return null;
  leaves.push(...sub);
  return leaves;
}

function leavesToIfStatement(leaves: readonly FlatLeaf[], pos: NodePosition): IfStatement {
  const [first, ...rest] = leaves;
  const elseIfBranches: { condition: Expression; body: readonly Statement[] }[] = [];
  let elseBranch: readonly Statement[] | null = null;
  for (const leaf of rest) {
    if (leaf.condition === null) {
      elseBranch = leaf.body;
    } else {
      elseIfBranches.push({ condition: leaf.condition, body: leaf.body });
    }
  }
  return {
    kind: 'IfStatement',
    condition: first!.condition!,
    thenBranch: first!.body,
    elseIfBranches,
    elseBranch,
    endLine: null,
    line: pos.line,
    column: pos.column,
  };
}

/**
 * "Αφαίρεση όλων των εμφωλευμένων ΑΝ" — ξεδιπλώνει ΟΠΟΙΟΔΗΠΟΤΕ βάθος
 * εμφωλευμένων ΑΝ (μέσα σε ΤΟΤΕ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ, με ή χωρίς άλλες εντολές
 * γύρω τους) σε ΜΙΑ επίπεδη Πολλαπλή. Κάθε κλάδος = η συνθήκη του γονιού
 * ΚΑΙ (μόνο αν χρειάζεται) η συνθήκη του παιδιού — τα "αδέρφια" (ΤΟΤΕ↔ΑΛΛΙΩΣ,
 * ή διαδοχικά ΑΛΛΙΩΣ_ΑΝ) ΔΕΝ χρειάζονται επιπλέον άρνηση, χάρη στη φυσική
 * σειρά αξιολόγησης του ΑΛΛΙΩΣ_ΑΝ (πρώτο ταίριασμα κερδίζει). `null` όποτε
 * δεν αποδεικνύεται ασφαλές (βλ. `flattenBranchBody`) — καμία μαντεψιά.
 * ΔΕΝ εξαλείφει "νεκρές" αναθέσεις (μια ανάθεση που αμέσως ξαναγράφεται σε
 * κάποιο κλάδο μπορεί να μείνει περιττή αλλά αβλαβής) — σκόπιμα, θα
 * χρειαζόταν ανάλυση χρήσης/ζωντάνιας μεταβλητών, εκτός του πεδίου εδώ.
 */
export function flattenNestedIf(stmt: IfStatement): IfStatement | null {
  const leaves = flattenIfToLeaves(stmt);
  if (leaves === null) return null;
  return leavesToIfStatement(leaves, stmt);
}

// --- Εμφωλευμένο ΑΝ σε ΤΟΤΕ ↔ Απλή (σύνθετη συνθήκη με ΚΑΙ) ------------------

/** ΑΝ{ΑΝ{...}} (εμφωλευμένο στο ΤΟΤΕ, χωρίς άλλα ΑΛΛΙΩΣ) → μία Απλή ΑΝ με ΚΑΙ. Απαιτεί `classifyIf(stmt) === 'nested-then'`. */
export function nestedThenToSimple(stmt: IfStatement): IfStatement {
  const inner = stmt.thenBranch[0] as IfStatement;
  return {
    kind: 'IfStatement',
    condition: { kind: 'BinaryExpression', operator: 'ΚΑΙ', left: stmt.condition, right: inner.condition, line: stmt.line, column: stmt.column },
    thenBranch: inner.thenBranch,
    elseIfBranches: [],
    elseBranch: null,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
}

/** Απλή ΑΝ με σύνθετη συνθήκη (α ΚΑΙ β) → δύο εμφωλευμένες Απλές ΑΝ. `null` αν η συνθήκη δεν είναι top-level ΚΑΙ, ή αν υπάρχει ΑΛΛΙΩΣ/ΑΛΛΙΩΣ_ΑΝ (δεν μεταφέρεται καθαρά στην εμφωλευμένη μορφή). */
export function simpleToNestedThen(stmt: IfStatement): IfStatement | null {
  if (stmt.elseBranch !== null || stmt.elseIfBranches.length > 0) return null;
  if (stmt.condition.kind !== 'BinaryExpression' || stmt.condition.operator !== 'ΚΑΙ') return null;
  const inner: IfStatement = {
    kind: 'IfStatement',
    condition: stmt.condition.right,
    thenBranch: stmt.thenBranch,
    elseIfBranches: [],
    elseBranch: null,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
  return {
    kind: 'IfStatement',
    condition: stmt.condition.left,
    thenBranch: [inner],
    elseIfBranches: [],
    elseBranch: null,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
}

// --- Σύνθετη/Πολλαπλή ↔ Απλή (η μόνη 1↔Ν μετατροπή) --------------------------

function simpleIf(condition: Expression, body: readonly Statement[], pos: IfStatement): IfStatement {
  return { kind: 'IfStatement', condition, thenBranch: body, elseIfBranches: [], elseBranch: null, endLine: null, line: pos.line, column: pos.column };
}

function notExpr(operand: Expression, pos: NodePosition): Expression {
  return { kind: 'UnaryExpression', operator: 'ΟΧΙ', operand, line: pos.line, column: pos.column };
}

function andExpr(left: Expression, right: Expression, pos: NodePosition): Expression {
  return { kind: 'BinaryExpression', operator: 'ΚΑΙ', left, right, line: pos.line, column: pos.column };
}

function collectReferencedNames(expr: Expression, out: Set<string>): void {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
      return;
    case 'Identifier':
      out.add(expr.name);
      return;
    case 'ArrayAccess':
      out.add(expr.array.name);
      for (const idx of expr.indices) collectReferencedNames(idx, out);
      return;
    case 'FunctionCall':
      for (const arg of expr.args) collectReferencedNames(arg, out);
      return;
    case 'UnaryExpression':
      collectReferencedNames(expr.operand, out);
      return;
    case 'BinaryExpression':
      collectReferencedNames(expr.left, out);
      collectReferencedNames(expr.right, out);
      return;
  }
}

export function referencedNames(expr: Expression): ReadonlySet<string> {
  const out = new Set<string>();
  collectReferencedNames(expr, out);
  return out;
}

function lvalueName(target: LValue): string {
  return target.kind === 'Identifier' ? target.name : target.array.name;
}

/** Ονόματα (μεταβλητές/πίνακες) που ΑΛΛΑΖΟΥΝ τιμή μέσα σε μια λίστα εντολών — αναδρομικά, μέσα από κάθε εμφωλευμένη δομή (ΑΝ/ΓΙΑ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ/ΕΠΙΛΕΞΕ), αφού μια μεταβολή "θαμμένη" βαθύτερα μέσα στον κλάδο μετράει το ίδιο. */
function collectAssignedNames(statements: readonly Statement[], out: Set<string>): void {
  for (const stmt of statements) {
    switch (stmt.kind) {
      case 'AssignmentStatement':
        out.add(lvalueName(stmt.target));
        break;
      case 'ReadStatement':
        for (const target of stmt.targets) out.add(lvalueName(target));
        break;
      case 'SwapStatement':
        out.add(lvalueName(stmt.left));
        out.add(lvalueName(stmt.right));
        break;
      case 'IfStatement':
        collectAssignedNames(stmt.thenBranch, out);
        for (const b of stmt.elseIfBranches) collectAssignedNames(b.body, out);
        if (stmt.elseBranch !== null) collectAssignedNames(stmt.elseBranch, out);
        break;
      case 'ForStatement':
        out.add(stmt.variable.name);
        collectAssignedNames(stmt.body, out);
        break;
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        collectAssignedNames(stmt.body, out);
        break;
      case 'SwitchStatement':
        for (const c of stmt.cases) collectAssignedNames(c.body, out);
        if (stmt.elseBody !== null) collectAssignedNames(stmt.elseBody, out);
        break;
      case 'WriteStatement':
      case 'CallStatement':
        break;
    }
  }
}

export function assignedNames(statements: readonly Statement[]): ReadonlySet<string> {
  const out = new Set<string>();
  collectAssignedNames(statements, out);
  return out;
}

function setsIntersect(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  for (const name of a) if (b.has(name)) return true;
  return false;
}

function collectNamesInSwitchCaseValue(value: SwitchCaseValue, out: Set<string>): void {
  if (value.kind === 'CaseRange') {
    collectReferencedNames(value.from, out);
    collectReferencedNames(value.to, out);
    return;
  }
  if (value.kind === 'CaseComparison') {
    collectReferencedNames(value.value, out);
    return;
  }
  collectReferencedNames(value, out);
}

function collectAllNamesInStatement(stmt: Statement, out: Set<string>): void {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      out.add(lvalueName(stmt.target));
      if (stmt.target.kind === 'ArrayAccess') for (const idx of stmt.target.indices) collectReferencedNames(idx, out);
      collectReferencedNames(stmt.value, out);
      return;
    case 'ReadStatement':
      for (const t of stmt.targets) {
        out.add(lvalueName(t));
        if (t.kind === 'ArrayAccess') for (const idx of t.indices) collectReferencedNames(idx, out);
      }
      return;
    case 'WriteStatement':
      for (const v of stmt.values) collectReferencedNames(v, out);
      return;
    case 'SwapStatement':
      out.add(lvalueName(stmt.left));
      out.add(lvalueName(stmt.right));
      if (stmt.left.kind === 'ArrayAccess') for (const idx of stmt.left.indices) collectReferencedNames(idx, out);
      if (stmt.right.kind === 'ArrayAccess') for (const idx of stmt.right.indices) collectReferencedNames(idx, out);
      return;
    case 'IfStatement':
      collectReferencedNames(stmt.condition, out);
      collectAllNames(stmt.thenBranch, out);
      for (const b of stmt.elseIfBranches) {
        collectReferencedNames(b.condition, out);
        collectAllNames(b.body, out);
      }
      if (stmt.elseBranch !== null) collectAllNames(stmt.elseBranch, out);
      return;
    case 'ForStatement':
      out.add(stmt.variable.name);
      collectReferencedNames(stmt.from, out);
      collectReferencedNames(stmt.to, out);
      if (stmt.step !== null) collectReferencedNames(stmt.step, out);
      collectAllNames(stmt.body, out);
      return;
    case 'WhileStatement':
      collectReferencedNames(stmt.condition, out);
      collectAllNames(stmt.body, out);
      return;
    case 'RepeatUntilStatement':
      collectAllNames(stmt.body, out);
      collectReferencedNames(stmt.condition, out);
      return;
    case 'SwitchStatement':
      collectReferencedNames(stmt.discriminant, out);
      for (const c of stmt.cases) {
        for (const v of c.values) collectNamesInSwitchCaseValue(v, out);
        collectAllNames(c.body, out);
      }
      if (stmt.elseBody !== null) collectAllNames(stmt.elseBody, out);
      return;
    case 'CallStatement':
      for (const a of stmt.args) collectReferencedNames(a, out);
      return;
  }
}

/** Όλα τα ονόματα (μεταβλητές/πίνακες) που εμφανίζονται ΟΠΟΥΔΗΠΟΤΕ σε μια λίστα εντολών — είτε ως ανάγνωση είτε ως στόχος ανάθεσης — χρησιμοποιείται για να επιλεγεί ένα φρέσκο, χωρίς συγκρούσεις, όνομα (βλ. `renameEligibleSimpleSequence`). */
export function collectAllNames(statements: readonly Statement[], out: Set<string> = new Set()): ReadonlySet<string> {
  for (const stmt of statements) collectAllNamesInStatement(stmt, out);
  return out;
}

/**
 * Το σύνολο των ονομάτων που εμφανίζονται σε μια ΑΚΟΛΟΥΘΙΑ κλάδων
 * (condition, body) όπου κάποιος ΠΡΟΗΓΟΥΜΕΝΟΣ κλάδος μεταβάλλει (ΑΝΑΘΕΣΗ,
 * ΔΙΑΒΑΣΕ, ή ΑΝΤΑΛΛΑΓΗ) μια μεταβλητή που εμφανίζεται στη συνθήκη ενός
 * ΕΠΟΜΕΝΟΥ κλάδου — οπότε το "ΑΝ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ σε μία αξιολόγηση ανά
 * κλάδο" ΔΕΝ είναι ισοδύναμο με "ανεξάρτητες Απλές ΑΝ που ξαναϋπολογίζουν
 * ΚΑΘΕ συνθήκη". Άδειο σύνολο σημαίνει "ασφαλές". Χρησιμοποιείται και προς
 * τις δύο κατευθύνσεις: `toSimpleSequence`/`renameEligibleSimpleSequence`
 * (μη σπάσεις κάτι που θα άλλαζε συμπεριφορά, εκτός αν μετονομάσεις) και
 * `findSimpleChains` (μην ενώσεις κάτι που θα άλλαζε συμπεριφορά) — βλ. bug
 * report του χρήστη: `Αν χ>5 Τότε χ←χ-2 Αλλιώς χ←χ*2` δίνει ΛΑΘΟΣ
 * αποτέλεσμα αν σπάσει σε δύο ανεξάρτητες ΑΝ, αφού η 2η συνθήκη (`Όχι χ>5`)
 * θα δει το ήδη μεταβλημένο χ.
 */
function branchMutationConflicts(branches: readonly { condition: Expression; body: readonly Statement[] }[]): ReadonlySet<string> {
  const conflicts = new Set<string>();
  for (let i = 0; i < branches.length; i++) {
    const mutated = assignedNames(branches[i]!.body);
    if (mutated.size === 0) continue;
    for (let j = i + 1; j < branches.length; j++) {
      const refs = referencedNames(branches[j]!.condition);
      for (const name of mutated) if (refs.has(name)) conflicts.add(name);
    }
  }
  return conflicts;
}

function hasUnsafeMutationAcrossBranches(branches: readonly { condition: Expression; body: readonly Statement[] }[]): boolean {
  return branchMutationConflicts(branches).size > 0;
}

/**
 * Ειδική περίπτωση του `branchMutationConflicts` για το `toSimpleSequence`,
 * με μία επιπλέον λεπτομέρεια: το ΑΛΛΙΩΣ (αν υπάρχει) δεν έχει ΔΙΚΗ ΤΟΥ
 * αποθηκευμένη συνθήκη στο AST — η έμμεση συνθήκη του είναι η άρνηση ΟΛΩΝ
 * των προηγούμενων κλάδων μαζί, άρα "διαβάζει" τις μεταβλητές ΟΛΩΝ τους. Αν
 * οποιοσδήποτε κλάδος (ΑΝ ή ΑΛΛΙΩΣ_ΑΝ) μεταβάλλει κάποια από αυτές, το
 * σπάσιμο είναι ανασφαλές, ακόμα κι αν δεν υπάρχει κανένα ΑΛΛΙΩΣ_ΑΝ ανάμεσά
 * τους (π.χ. απλή Σύνθετη με 2 κλάδους).
 */
function conflictNamesForSplit(stmt: IfStatement): ReadonlySet<string> {
  const branches: readonly { condition: Expression; body: readonly Statement[] }[] = [
    { condition: stmt.condition, body: stmt.thenBranch },
    ...stmt.elseIfBranches,
  ];
  const conflicts = new Set(branchMutationConflicts(branches));
  if (stmt.elseBranch !== null) {
    const allConditionNames = new Set<string>();
    for (const b of branches) for (const name of referencedNames(b.condition)) allConditionNames.add(name);
    for (const b of branches) for (const name of assignedNames(b.body)) if (allConditionNames.has(name)) conflicts.add(name);
  }
  return conflicts;
}

function hasUnsafeMutationForSplit(stmt: IfStatement): boolean {
  return conflictNamesForSplit(stmt).size > 0;
}

const NO_RENAMES: ReadonlyMap<string, string> = new Map();

function renameAssignmentTarget(body: readonly Statement[], oldName: string, newName: string): readonly Statement[] {
  return body.map((s) => {
    if (s.kind === 'AssignmentStatement' && s.target.kind === 'Identifier' && s.target.name === oldName) {
      return { ...s, target: { ...s.target, name: newName } };
    }
    return s;
  });
}

function applyRenames(body: readonly Statement[], renames: ReadonlyMap<string, string>): readonly Statement[] {
  let result = body;
  for (const [oldName, newName] of renames) result = renameAssignmentTarget(result, oldName, newName);
  return result;
}

/**
 * Κοινός πυρήνας του `toSimpleSequence`, ΧΩΡΙΣ τον έλεγχο ασφάλειας (τον
 * κάνει ο καλών) — δέχεται επιπλέον ένα προαιρετικό `renames` (παλιό→νέο
 * όνομα) που εφαρμόζεται ΜΟΝΟ στους στόχους ΑΝΑΘΕΣΗΣ μέσα σε κάθε κλάδο
 * (όχι στις συνθήκες, ούτε στο δεξί μέλος εκφράσεων) — βλ.
 * `renameEligibleSimpleSequence`.
 */
function buildSimpleSequenceCore(stmt: IfStatement, renames: ReadonlyMap<string, string>): readonly IfStatement[] {
  const branches: readonly { condition: Expression; body: readonly Statement[] }[] = [
    { condition: stmt.condition, body: stmt.thenBranch },
    ...stmt.elseIfBranches,
  ];

  const result: IfStatement[] = [];
  let priorNegations: Expression | null = null;
  for (const branch of branches) {
    const condition = priorNegations === null ? branch.condition : andExpr(priorNegations, branch.condition, stmt);
    result.push(simpleIf(condition, applyRenames(branch.body, renames), stmt));
    const negated = negateCondition(branch.condition, stmt);
    priorNegations = priorNegations === null ? negated : andExpr(priorNegations, negated, stmt);
  }
  if (stmt.elseBranch !== null) {
    result.push(simpleIf(priorNegations!, applyRenames(stmt.elseBranch, renames), stmt));
  }
  return result;
}

/**
 * ΑΝ...ΑΛΛΙΩΣ_ΑΝ...ΑΛΛΙΩΣ (Σύνθετη ή Πολλαπλή, οποιουδήποτε αριθμού κλάδων)
 * → μία ανεξάρτητη Απλή ΑΝ ανά κλάδο, η καθεμία με τη δική της συνθήκη ΚΑΙ
 * την άρνηση όλων των προηγούμενων (ώστε να παραμείνουν αμοιβαία
 * αποκλειόμενες, ακριβώς όπως στο αρχικό ΑΝ/ΑΛΛΙΩΣ_ΑΝ). Γενικεύει τη
 * "Σύνθετη→Απλή" (N=2) σε οποιοδήποτε πλήθος κλάδων. Επιστρέφει `null` όταν
 * το σπάσιμο θα άλλαζε τη συμπεριφορά (βλ. `hasUnsafeMutationForSplit`) —
 * βλ. `renameEligibleSimpleSequence` για μια εναλλακτική που προσπαθεί να
 * διορθώσει αυτή την περίπτωση με αυτόματη μετονομασία.
 */
export function toSimpleSequence(stmt: IfStatement): readonly IfStatement[] | null {
  if (hasUnsafeMutationForSplit(stmt)) return null;
  return buildSimpleSequenceCore(stmt, NO_RENAMES);
}

function allBranchBodies(stmt: IfStatement): readonly (readonly Statement[])[] {
  const bodies: (readonly Statement[])[] = [stmt.thenBranch, ...stmt.elseIfBranches.map((b) => b.body)];
  if (stmt.elseBranch !== null) bodies.push(stmt.elseBranch);
  return bodies;
}

/**
 * true αν το `body` κάνει ΑΚΡΙΒΩΣ μία, top-level, "καθαρή" ανάθεση στο
 * `name` (ΑΝΑΘΕΣΗ σε απλή μεταβλητή, όχι σε στοιχείο πίνακα) και ΤΙΠΟΤΑ
 * άλλο μέσα στο `body` δεν διαβάζει ή ξαναγράφει το `name` — το μοτίβο
 * "x ← x ± κάτι" και μόνο. Αν ισχύει, η μετονομασία του στόχου ανάθεσης σε
 * ένα φρέσκο όνομα δεν αλλάζει τίποτα άλλο μέσα στον κλάδο.
 */
function isSingleCleanAssignmentTo(body: readonly Statement[], name: string): boolean {
  const assignmentIndices: number[] = [];
  for (let i = 0; i < body.length; i++) {
    const s = body[i]!;
    if (s.kind === 'AssignmentStatement' && s.target.kind === 'Identifier' && s.target.name === name) {
      assignmentIndices.push(i);
    }
  }
  if (assignmentIndices.length !== 1) return false;
  const assignIndex = assignmentIndices[0]!;
  for (let i = 0; i < body.length; i++) {
    if (i === assignIndex) continue;
    if (collectAllNames([body[i]!]).has(name)) return false;
  }
  return true;
}

function freshVariableName(base: string, avoid: ReadonlySet<string>): string {
  let i = 2;
  let candidate = `${base}${i}`;
  while (avoid.has(candidate)) {
    i++;
    candidate = `${base}${i}`;
  }
  return candidate;
}

/**
 * Εναλλακτική του `toSimpleSequence` για ΑΚΡΙΒΩΣ την περίπτωση που εκείνο
 * αρνείται (`conflictNamesForSplit` μη κενό): αν ΚΑΘΕ κλάδος κάνει μία
 * "καθαρή" ανάθεση στη μεταβλητή-σύγκρουση (βλ. `isSingleCleanAssignmentTo`),
 * η μετατροπή γίνεται εφικτή μετονομάζοντας τον στόχο της ανάθεσης σε ένα
 * φρέσκο όνομα σε ΚΑΘΕ κλάδο — η αρχική μεταβλητή δεν ξαναγράφεται πια, άρα
 * οι συνθήκες που τη διαβάζουν παραμένουν σωστές σε κάθε ανεξάρτητη Απλή
 * ΑΝ. Το `avoidNames` πρέπει να περιέχει ΟΛΑ τα ονόματα που ήδη
 * χρησιμοποιούνται οπουδήποτε στο πρόγραμμα, ώστε το φρέσκο όνομα να μην
 * συγκρουστεί με τίποτα. Επιστρέφει `null` αν κάποιος κλάδος δεν κάνει
 * "καθαρή" ανάθεση (π.χ. μεταβάλλει τη μεταβλητή μέσα σε εμφωλευμένη δομή,
 * ή τη χρησιμοποιεί ξανά μετά την ανάθεση) — τότε δεν προσφέρεται καθόλου η
 * μετατροπή, ακριβώς όπως πριν.
 */
export function renameEligibleSimpleSequence(
  stmt: IfStatement,
  avoidNames: ReadonlySet<string>,
): { readonly statements: readonly IfStatement[]; readonly renames: ReadonlyMap<string, string> } | null {
  const conflicts = conflictNamesForSplit(stmt);
  if (conflicts.size === 0) return null;
  const bodies = allBranchBodies(stmt);
  const renames = new Map<string, string>();
  const used = new Set(avoidNames);
  for (const name of conflicts) {
    if (!bodies.every((b) => isSingleCleanAssignmentTo(b, name))) return null;
    const fresh = freshVariableName(name, used);
    used.add(fresh);
    renames.set(name, fresh);
  }
  return { statements: buildSimpleSequenceCore(stmt, renames), renames };
}

/**
 * Επιστρέφει τον κόμβο (σε μορφή Σύνθετη/Πολλαπλή) πάνω στον οποίο έχει
 * νόημα το `toSimpleSequence`/`renameEligibleSimpleSequence`, για
 * ΟΠΟΙΑΔΗΠΟΤΕ από τις μορφές που τελικά καταλήγουν σε "→ Απλή"
 * (composite/multiple/nested-else/switch, ίδια λογική με το
 * `availableSingleNodeConversions`) — `null` για τις υπόλοιπες
 * (simple/nested-then), ή αν το ΕΠΙΛΕΞΕ δεν είναι μετατρέψιμο σε Πολλαπλή.
 */
export function asSimpleSequenceCandidate(stmt: IfStatement | SwitchStatement): IfStatement | null {
  if (stmt.kind === 'SwitchStatement') return switchToMultiple(stmt);
  const shape = classifyIf(stmt);
  if (shape === 'nested-else') return nestedElseToMultiple(stmt);
  if (shape === 'composite' || shape === 'multiple') return stmt;
  return null;
}

/** Σπάει μια αριστερόστροφη αλυσίδα ΚΑΙ (όπως παράγει το `toSimpleSequence`/ο printer) σε επίπεδη λίστα conjuncts, με τη σειρά. */
function splitAndChain(expr: Expression): Expression[] {
  if (expr.kind === 'BinaryExpression' && expr.operator === 'ΚΑΙ') {
    return [...splitAndChain(expr.left), expr.right];
  }
  return [expr];
}

/**
 * Αν το `condition` έχει ακριβώς τη μορφή που παράγει το `toSimpleSequence`
 * για έναν κλάδο μετά από `priorConditions` προηγούμενες (κάθε ¬prior[k]
 * μπορεί να είναι είτε ρητό ΟΧΙ είτε αντίστροφος συγκριτικός τελεστής):
 * - ¬prior[0] ∧ ... ∧ ¬prior[N-1] ∧ X  (ένας ΑΛΛΙΩΣ_ΑΝ-κλάδος με δική του
 *   συνθήκη X) → επιστρέφει `{ expr: X }`·
 * - ¬prior[0] ∧ ... ∧ ¬prior[N-1]  (ΧΩΡΙΣ επιπλέον δική του συνθήκη — αυτό
 *   παράγει το `toSimpleSequence` για τον τελικό ΑΛΛΙΩΣ-κλάδο) →
 *   επιστρέφει `'else'`·
 * - αλλιώς `null`.
 */
function extractRealCondition(
  condition: Expression,
  priorConditions: readonly Expression[],
): { readonly expr: Expression } | 'else' | null {
  const conjuncts = splitAndChain(condition);
  const matchesPriors = (count: number): boolean => {
    for (let k = 0; k < count; k++) {
      if (!isDirectNegation(priorConditions[k]!, conjuncts[k]!)) return false;
    }
    return true;
  };
  if (conjuncts.length === priorConditions.length + 1 && matchesPriors(priorConditions.length)) {
    return { expr: conjuncts[conjuncts.length - 1]! };
  }
  if (conjuncts.length === priorConditions.length && matchesPriors(priorConditions.length)) {
    return 'else';
  }
  return null;
}

/** Ενώνει μια ήδη επιβεβαιωμένη αλυσίδα (βλ. `findSimpleChains`) πίσω σε μία Σύνθετη (2 στοιχεία) ή Πολλαπλή (3+) ΑΝ. */
function mergeSimpleChain(chain: readonly IfStatement[]): IfStatement {
  const first = chain[0]!;
  const priorConditions: Expression[] = [first.condition];
  const elseIfBranches: { condition: Expression; body: readonly Statement[] }[] = [];
  let elseBranch: readonly Statement[] | null = null;

  for (let i = 1; i < chain.length; i++) {
    const real = extractRealCondition(chain[i]!.condition, priorConditions);
    if (real === 'else') {
      elseBranch = chain[i]!.thenBranch;
    } else {
      elseIfBranches.push({ condition: real!.expr, body: chain[i]!.thenBranch });
      priorConditions.push(real!.expr);
    }
  }

  return {
    kind: 'IfStatement',
    condition: first.condition,
    thenBranch: first.thenBranch,
    elseIfBranches,
    elseBranch,
    endLine: null,
    line: first.line,
    column: first.column,
  };
}

export interface SimpleChainConversion {
  /** Οι διαδοχικές Απλές ΑΝ (2 ή περισσότερες) που θα ενωθούν σε μία. */
  readonly statements: readonly IfStatement[];
  readonly apply: () => IfStatement;
}

function isPlainComparison(op: BinaryOperator): boolean {
  return op === '=' || isComparisonOperator(op);
}

/** V (ή null αν η έκφραση δεν είναι ακριβώς μια απλή μεταβλητή). */
function asSimpleVariable(expr: Expression): string | null {
  return expr.kind === 'Identifier' ? expr.name : null;
}

function asNumberLiteral(expr: Expression): number | null {
  return expr.kind === 'NumberLiteral' ? expr.value : null;
}

/**
 * Αν το `expr` είναι ακριβώς `V + σταθερά`, `σταθερά + V`, ή `V - σταθερά`
 * (ΟΧΙ `σταθερά - V`, που θα ήταν αναστροφή προσήμου, όχι απλή μετατόπιση),
 * επιστρέφει την προσημασμένη μετατόπιση Δ ώστε `expr` ≡ `V + Δ`. Αλλιώς
 * `null` — η μεταβολή δεν είναι απλή γραμμική ολίσθηση, οπότε ο περιορισμένος
 * αλγεβρικός λύτης (βλ. `algebraicMergePair`) δεν προσπαθεί καν.
 */
export function asLinearShift(expr: Expression, variable: string): number | null {
  if (expr.kind !== 'BinaryExpression') return null;
  if (expr.operator === '+') {
    if (asSimpleVariable(expr.left) === variable) {
      const k = asNumberLiteral(expr.right);
      return k;
    }
    if (asSimpleVariable(expr.right) === variable) {
      const k = asNumberLiteral(expr.left);
      return k;
    }
    return null;
  }
  if (expr.operator === '-' && asSimpleVariable(expr.left) === variable) {
    const k = asNumberLiteral(expr.right);
    return k === null ? null : -k;
  }
  return null;
}

/**
 * true αν "V op (k-Δ)" είναι ΥΠΟΣΥΝΟΛΟ του "V op k" — δηλαδή αν η μετατόπιση
 * κατά Δ πάει το όριο "προς τα μέσα" στην κατεύθυνση που ήδη κοιτάει ο
 * τελεστής, όχι "προς τα έξω". Μόνο τότε είναι ασφαλές να χρησιμοποιηθεί το
 * μετατοπισμένο όριο ΜΟΝΟ ΤΟΥ (χωρίς σύζευξη ΚΑΙ με το αρχικό `V op k`) ως
 * μια αυτόνομη, πιο "καθαρή" συνθήκη — βλ. `algebraicMergePair`.
 *
 * Παράδειγμα όπου ΔΕΝ ισχύει (bug report χρήστη): `Αν χ>10 ... χ←χ+5 ...`
 * — το Δ=+5 σπρώχνει το χ ΜΑΚΡΙΑ από το 10, όχι κοντά· "χ>5" ΔΕΝ
 * συνεπάγεται "χ>10" (π.χ. χ=7 ικανοποιεί το πρώτο αλλά όχι το δεύτερο).
 */
function shiftStaysWithinOriginal(operator: BinaryOperator, delta: number): boolean {
  switch (operator) {
    case '>':
    case '>=':
      return delta <= 0;
    case '<':
    case '<=':
      return delta >= 0;
    default: // '=', '<>' — δεν έχουν "κατεύθυνση", η μετατοπισμένη μορφή δεν είναι ποτέ υποσύνολο εκτός αν Δ=0.
      return delta === 0;
  }
}

/**
 * true αν "V op1 k1 ΚΑΙ V op2 k2" (όπου op2 είναι ΠΑΝΤΑ η άρνηση του op1 σε
 * αυτό το πλαίσιο) δεν έχει ΚΑΜΙΑ λύση — π.χ. "V>10 ΚΑΙ V<=5" είναι αδύνατο
 * αφού κανένα V δεν μπορεί να είναι ταυτόχρονα μεγαλύτερο του 10 και
 * μικρότερο-ή-ίσο του 5. Όταν συμβαίνει αυτό (bug report χρήστη: `χ ← χ + 5`
 * με σύγκριση `>` σπρώχνει το όριο "προς τα έξω", καθιστώντας τον κλάδο
 * "και οι δύο εντολές τρέχουν" αδύνατο), δεν έχει νόημα να εμφανίζεται καν
 * ως κλάδος — βλ. `algebraicMergePair`, όπου τότε παράγεται απλή Σύνθετη.
 */
function comparisonRegionIsEmpty(operator: BinaryOperator, k1: number, k2: number): boolean {
  switch (operator) {
    case '>':
    case '>=':
      return k2 <= k1;
    case '<':
    case '<=':
      return k2 >= k1;
    case '=':
    case '<>':
      return k1 === k2;
    default:
      return true;
  }
}

/**
 * Περιορισμένος αλγεβρικός "λύτης" για ΑΚΡΙΒΩΣ την περίπτωση: δύο διαδοχικές
 * Απλές ΑΝ όπου η 2η συνθήκη είναι η ρητή άρνηση της 1ης (θα ταίριαζαν στο
 * μοτίβο Σύνθετη→Απλή, βλ. `findSimpleChains`), ΑΛΛΑ η 1η αλλάζει μια
 * μεταβλητή V που εμφανίζεται και στη 2η συνθήκη — η απλή συγχώνευση σε
 * Σύνθετη θα άλλαζε συμπεριφορά (βλ. bug report χρήστη: `Αν χ>10 ... Αν
 * χ<=10 ...` ΔΕΝ είναι το ίδιο με `Αν χ>10 ... Αλλιώς ...`, αφού η 2η
 * συνθήκη βλέπει το ΗΔΗ μεταβλημένο χ).
 *
 * Λειτουργεί ΜΟΝΟ όταν η μεταβολή είναι μια απλή γραμμική ολίσθηση
 * (`V ← V ± σταθερά`, βλ. `asLinearShift`) και η 1η συνθήκη είναι μια απλή
 * σύγκριση `V <συγκρ.> σταθερά` — τότε λύνει αλγεβρικά "θα ίσχυε η 2η
 * συνθήκη ΜΕΤΑ τη μετατόπιση;" (απλή πρόσθεση/αφαίρεση και στα δύο μέλη,
 * χωρίς αναστροφή φοράς — ασφαλές ΜΟΝΟ για πρόσθεση/αφαίρεση, ΟΧΙ για
 * πολλαπλασιασμό/διαίρεση που θα μπορούσαν να αναστρέψουν τη φορά της
 * ανισότητας ανάλογα με το πρόσημο).
 *
 * Χτίζει μια δομή που αναπαράγει ΑΚΡΙΒΩΣ τη σειριακή συμπεριφορά του
 * πρωτοτύπου: απλή Σύνθετη (2 κλάδοι) αν ο κλάδος "και οι δύο εντολές
 * τρέχουν" είναι στην πραγματικότητα αδύνατος (βλ. `comparisonRegionIsEmpty`
 * — π.χ. bug report χρήστη: `V ← V + σταθερά` με σύγκριση `>` σπρώχνει το
 * όριο "προς τα έξω"), αλλιώς Πολλαπλή με 3 αμοιβαία αποκλειόμενους κλάδους,
 * σε "καθαρή" μορφή (χωρίς σύνθετη ΚΑΙ συνθήκη) όποτε είναι αλγεβρικά
 * ασφαλές (βλ. `shiftStaysWithinOriginal`), αλλιώς σε μια εξίσου σωστή αλλά πιο
 * "μηχανική" μορφή με ρητή σύζευξη ΚΑΙ. Επιστρέφει `null` αν οποιαδήποτε
 * από τις προϋποθέσεις εφαρμογής δεν ισχύει — δεν μαντεύει.
 */
function algebraicMergePair(first: IfStatement, second: IfStatement): IfStatement | null {
  if (!isDirectNegation(first.condition, second.condition)) return null;
  if (first.condition.kind !== 'BinaryExpression' || !isPlainComparison(first.condition.operator)) return null;

  const variable = asSimpleVariable(first.condition.left);
  const k1 = asNumberLiteral(first.condition.right);
  if (variable === null || k1 === null) return null;

  if (first.thenBranch.length !== 1) return null;
  const onlyStatement = first.thenBranch[0]!;
  if (onlyStatement.kind !== 'AssignmentStatement' || onlyStatement.target.kind !== 'Identifier' || onlyStatement.target.name !== variable) {
    return null;
  }
  const delta = asLinearShift(onlyStatement.value, variable);
  if (delta === null) return null;

  const negatedOp = negateComparisonOperator(first.condition.operator);
  if (negatedOp === null) return null;
  const k2 = k1 - delta;
  const shiftedThreshold: Expression = { kind: 'NumberLiteral', value: k2, line: first.line, column: first.column };
  const variableExpr: Expression = { kind: 'Identifier', name: variable, line: first.line, column: first.column };
  const bothRun = [...first.thenBranch, ...second.thenBranch];

  // Αν ο κλάδος "και οι δύο εντολές τρέχουν" είναι στην πραγματικότητα
  // ΑΔΥΝΑΤΟΣ (καμία τιμή δεν ικανοποιεί ΚΑΙ τις δύο συνθήκες), δεν έχει
  // νόημα να τον γράψουμε καν — η μετατόπιση μαζεύτηκε "προς τα έξω"
  // (bug report χρήστη: `V ← V + σταθερά` με σύγκριση `>`), οπότε η 2η ΑΝ
  // ΠΟΤΕ δεν ξαναφαίνεται να ισχύει μετά τη μεταβολή — απλή Σύνθετη αρκεί.
  if (comparisonRegionIsEmpty(first.condition.operator, k1, k2)) {
    return {
      kind: 'IfStatement',
      condition: first.condition,
      thenBranch: first.thenBranch,
      elseIfBranches: [],
      elseBranch: second.thenBranch,
      endLine: null,
      line: first.line,
      column: first.column,
    };
  }

  if (shiftStaysWithinOriginal(first.condition.operator, delta)) {
    // Ασφαλές να γράψουμε το μετατοπισμένο όριο ΜΟΝΟ ΤΟΥ ως εξωτερική
    // συνθήκη (ίδιος τελεστής με την 1η συνθήκη) — φωλιάζουμε τους 3
    // αμοιβαία αποκλειόμενους κλάδους ΧΩΡΙΣ σύνθετη (ΚΑΙ) συνθήκη πουθενά.
    const outerCondition: Expression = {
      kind: 'BinaryExpression',
      operator: first.condition.operator,
      left: variableExpr,
      right: shiftedThreshold,
      line: first.line,
      column: first.column,
    };
    return {
      kind: 'IfStatement',
      condition: outerCondition,
      thenBranch: first.thenBranch,
      elseIfBranches: [{ condition: first.condition, body: bothRun }],
      elseBranch: second.thenBranch,
      endLine: null,
      line: first.line,
      column: first.column,
    };
  }

  // Δεν είναι ασφαλές να απλοποιηθεί σε μη-συζευγμένη μορφή (π.χ. `V ← V + σταθερά`
  // με σύγκριση `>`) — χρησιμοποιούμε τη ρητή σύζευξη ΚΑΙ, που είναι ΠΑΝΤΑ σωστή.
  const derivedCondition: Expression = { kind: 'BinaryExpression', operator: negatedOp, left: variableExpr, right: shiftedThreshold, line: first.line, column: first.column };
  return {
    kind: 'IfStatement',
    condition: andExpr(first.condition, derivedCondition, first),
    thenBranch: bothRun,
    elseIfBranches: [{ condition: first.condition, body: first.thenBranch }],
    elseBranch: second.thenBranch,
    endLine: null,
    line: first.line,
    column: first.column,
  };
}

/**
 * Βρίσκει τις ΜΕΓΙΣΤΕΣ διαδοχικές αλυσίδες Απλών ΑΝ (μέσα σε μια λίστα
 * εντολών, όχι αναδρομικά — βλ. `detectSelectionStructures`) όπου κάθε
 * επόμενη συνθήκη είναι ακριβώς η άρνηση όλων των προηγούμενων ΚΑΙ η δική
 * της πραγματική συνθήκη — δηλαδή το ακριβές αντίστροφο του
 * `toSimpleSequence`. Αλυσίδες μήκους 2 ενώνονται σε Σύνθετη, 3+ σε Πολλαπλή.
 */
export function findSimpleChains(statements: readonly Statement[]): SimpleChainConversion[] {
  const chains: SimpleChainConversion[] = [];
  let i = 0;
  while (i < statements.length) {
    const stmt = statements[i];
    if (stmt === undefined || stmt.kind !== 'IfStatement' || classifyIf(stmt) !== 'simple') {
      i++;
      continue;
    }

    const chain: IfStatement[] = [stmt];
    const priorConditions: Expression[] = [stmt.condition];
    let j = i + 1;
    while (j < statements.length) {
      const next = statements[j];
      if (next === undefined || next.kind !== 'IfStatement' || classifyIf(next) !== 'simple') break;
      const real = extractRealCondition(next.condition, priorConditions);
      if (real === null) break;
      chain.push(next);
      j++;
      if (real === 'else') break; // Ο ΑΛΛΙΩΣ-κλάδος είναι πάντα τελευταίος — καμία περαιτέρω άρνηση δεν έχει νόημα μετά.
      priorConditions.push(real.expr);
    }

    // Αν η ένωση θα άλλαζε συμπεριφορά (κάποιος κλάδος μεταβάλλει μεταβλητή
    // που εμφανίζεται σε συνθήκη επόμενου κλάδου — βλ. `hasUnsafeMutationAcrossBranches`),
    // δεν την προσφέρουμε απλά όπως πριν — δοκιμάζουμε πρώτα τον περιορισμένο
    // αλγεβρικό λύτη (βλ. `algebraicMergePair`), ΜΟΝΟ για ζεύγος (2 στοιχεία).
    const unsafe = chain.length >= 2 && hasUnsafeMutationAcrossBranches(chain.map((s) => ({ condition: s.condition, body: s.thenBranch })));
    if (chain.length >= 2 && !unsafe) {
      chains.push({ statements: chain, apply: () => mergeSimpleChain(chain) });
      i = j; // προσπερνάμε ΟΛΗ την αλυσίδα — όχι στοιχείο-στοιχείο, ώστε να μην ανιχνεύονται επικαλυπτόμενες υπο-αλυσίδες.
    } else if (unsafe && chain.length === 2) {
      const algebraic = algebraicMergePair(chain[0]!, chain[1]!);
      if (algebraic !== null) {
        chains.push({ statements: chain, apply: () => algebraic });
        i = j;
      } else {
        i++;
      }
    } else {
      i++;
    }
  }
  return chains;
}

// --- Διαδοχικές Απλές ΑΝ με "εμφωλευμένες" (υποσύνολο) συνθήκες ------------

/**
 * Ίδια ιδέα με `numericConstant` στο repetition.ts — ΔΕΝ την εισάγουμε από
 * εκεί για να αποφύγουμε κυκλική εξάρτηση (το repetition.ts ήδη εισάγει ΑΠΟ
 * εδώ). Πολύ μικρή συνάρτηση, ασφαλές να διπλασιαστεί.
 */
function localNumericConstant(expr: Expression): number | null {
  if (expr.kind === 'NumberLiteral') return expr.value;
  if (expr.kind === 'UnaryExpression' && expr.operator === '-' && expr.operand.kind === 'NumberLiteral') return -expr.operand.value;
  return null;
}

type NumericComparisonOperator = '<' | '<=' | '>' | '>=' | '=';

interface SimpleNumericComparison {
  readonly left: Expression;
  readonly op: NumericComparisonOperator;
  readonly k: number;
}

/** Αν η συνθήκη είναι ΑΚΡΙΒΩΣ μια απλή αριθμητική σύγκριση (<,<=,>,>=,=) μιας έκφρασης έναντι ΣΤΑΘΕΡΑΣ — αλλιώς `null` (π.χ. <> ή σύνθετη ΚΑΙ/Η δεν είναι απλό διάστημα, δεν υποστηρίζονται εδώ). */
function asSimpleNumericComparison(condition: Expression): SimpleNumericComparison | null {
  if (condition.kind !== 'BinaryExpression') return null;
  const op = condition.operator;
  if (op !== '<' && op !== '<=' && op !== '>' && op !== '>=' && op !== '=') return null;
  const k = localNumericConstant(condition.right);
  if (k === null) return null;
  return { left: condition.left, op, k };
}

function evaluateNumericComparison(op: NumericComparisonOperator, x: number, k: number): boolean {
  switch (op) {
    case '<':
      return x < k;
    case '<=':
      return x <= k;
    case '>':
      return x > k;
    case '>=':
      return x >= k;
    case '=':
      return x === k;
  }
}

/**
 * true αν "αριστερή έκφραση `a.op` a.k" ΣΥΝΕΠΑΓΕΤΑΙ "ίδια έκφραση `b.op` b.k"
 * — για ΚΑΘΕ δυνατή τιμή (απλή ανάλυση διαστήματος: `<`/`<=` = ημιευθεία
 * προς -∞, `>`/`>=` = ημιευθεία προς +∞, `=` = ένα σημείο). ΠΡΟΣΟΧΗ: η φορά
 * μετράει — a⟹b δεν σημαίνει b⟹a.
 */
function numericComparisonImplies(a: SimpleNumericComparison, b: SimpleNumericComparison): boolean {
  if (a.op === '=') return evaluateNumericComparison(b.op, a.k, b.k);
  if (b.op === '=') return false; // ημιευθεία (άπειρη) ποτέ δεν είναι υποσύνολο ενός μεμονωμένου σημείου
  const aIsLower = a.op === '<' || a.op === '<=';
  const bIsLower = b.op === '<' || b.op === '<=';
  if (aIsLower !== bIsLower) return false; // αντίθετες κατευθύνσεις: άπειρο προς τη μία ΔΕΝ χωράει σε άπειρο προς την άλλη
  if (aIsLower) {
    if (a.k < b.k) return true;
    if (a.k > b.k) return false;
    return a.op === '<' || b.op === '<='; // ίδιο όριο: αρκεί το a να ΜΗΝ το περιλαμβάνει, Ή το b να το περιλαμβάνει
  }
  if (a.k > b.k) return true;
  if (a.k < b.k) return false;
  return a.op === '>' || b.op === '>=';
}

/** true αν "αριστερή έκφραση `a.op` a.k" ΚΑΙ "ίδια έκφραση `b.op` b.k" είναι αμοιβαία αποκλειόμενα (καμία τιμή δεν ικανοποιεί και τις δύο). Συμμετρική. */
function numericComparisonsDisjoint(a: SimpleNumericComparison, b: SimpleNumericComparison): boolean {
  if (a.op === '=') return !evaluateNumericComparison(b.op, a.k, b.k);
  if (b.op === '=') return !evaluateNumericComparison(a.op, b.k, a.k);
  const aIsLower = a.op === '<' || a.op === '<=';
  const bIsLower = b.op === '<' || b.op === '<=';
  if (aIsLower === bIsLower) return false; // ίδια κατεύθυνση: πάντα επικαλύπτονται προς το άπειρο
  const lower = aIsLower ? a : b;
  const upper = aIsLower ? b : a;
  if (lower.k < upper.k) return true;
  if (lower.k > upper.k) return false;
  return !(lower.op === '<=' && upper.op === '>='); // ίδιο όριο: αποκλείονται ΕΚΤΟΣ αν το ίδιο το όριο ανήκει και στα δύο
}

/**
 * Ενώνει μια ήδη επιβεβαιωμένη "εμφωλευμένη" αλυσίδα (βλ. `findNestedSimpleChains`)
 * σε ΜΙΑ Σύνθετη/Πολλαπλή ΑΝ. Επειδή οι συνθήκες ΔΕΝ είναι αμοιβαία
 * αποκλειόμενες (π.χ. "x<=5" είναι υποσύνολο του "x<=10"), κάθε κλάδος i
 * παίρνει το ΔΙΚΟ του σώμα ΣΥΝ τα σώματα κάθε μεταγενέστερου κλάδου j για τον
 * οποίο αποδεικνύεται ότι η συνθήκη του i συνεπάγεται τη συνθήκη του j — έτσι
 * τρέχουν μαζί, ΑΚΡΙΒΩΣ όπως έτρεχαν όταν ήταν ανεξάρτητες εντολές.
 */
function mergeNestedSimpleChain(chain: readonly IfStatement[], comparisons: readonly SimpleNumericComparison[]): IfStatement {
  const mergedBodies: Statement[][] = chain.map((stmt) => [...stmt.thenBranch]);
  for (let i = 0; i < chain.length; i++) {
    for (let j = i + 1; j < chain.length; j++) {
      if (numericComparisonImplies(comparisons[i]!, comparisons[j]!)) {
        mergedBodies[i]!.push(...chain[j]!.thenBranch);
      }
    }
  }

  const first = chain[0]!;
  const elseIfBranches = chain.slice(1).map((stmt, idx) => ({ condition: stmt.condition, body: mergedBodies[idx + 1]! }));

  return {
    kind: 'IfStatement',
    condition: first.condition,
    thenBranch: mergedBodies[0]!,
    elseIfBranches,
    elseBranch: null,
    endLine: null,
    line: first.line,
    column: first.column,
  };
}

/**
 * Βρίσκει διαδοχικές Απλές ΑΝ όπου οι συνθήκες τους (ίδια αριστερή μεταβλητή,
 * απλή αριθμητική σύγκριση με σταθερά) έχουν αποδεδειγμένη σχέση "συνεπαγωγής"
 * ή "αμοιβαίου αποκλεισμού" ανά ζεύγη — π.χ. "x<=5"/"x<=10"/"x>10" (το πρώτο
 * συνεπάγεται το δεύτερο, και τα δύο αποκλείονται από το τρίτο). Σε αντίθεση
 * με το `findSimpleChains` (που απαιτεί ΡΗΤΑ αποκλειστικές συνθήκες γραμμένες
 * σαν ΟΧΙ(προηγούμενης) ΚΑΙ ...), εδώ οι συνθήκες μπορεί να είναι
 * "εμφωλευμένες" — η ένωση αντιγράφει τα σώματα των συνεπαγόμενων κλάδων στον
 * πιο "στενό" (βλ. `mergeNestedSimpleChain`). Η σειρά μετράει: μόνο ΠΡΟΣ ΤΑ
 * ΕΜΠΡΟΣ συνεπαγωγή (νωρίτερη ⟹ μεταγενέστερη) γίνεται δεκτή — η αντίστροφη
 * σειρά (πλατύτερη συνθήκη πρώτη) ΔΕΝ ενώνεται με ασφάλεια, αρνούμαστε.
 * Αρνείται ΕΠΙΣΗΣ αν κάποιο σώμα της αλυσίδας ξαναγράφει την ίδια τη
 * συγκρινόμενη μεταβλητή (θα ακύρωνε τη στατική απόδειξη — decline don't guess).
 */
export function findNestedSimpleChains(statements: readonly Statement[]): SimpleChainConversion[] {
  const chains: SimpleChainConversion[] = [];
  let i = 0;
  while (i < statements.length) {
    const stmt = statements[i];
    if (stmt === undefined || stmt.kind !== 'IfStatement' || classifyIf(stmt) !== 'simple') {
      i++;
      continue;
    }
    const firstCmp = asSimpleNumericComparison(stmt.condition);
    if (firstCmp === null || firstCmp.left.kind !== 'Identifier') {
      i++;
      continue;
    }
    const varName = firstCmp.left.name;
    if (assignedNames(stmt.thenBranch).has(varName)) {
      i++;
      continue;
    }

    const chain: IfStatement[] = [stmt];
    const comparisons: SimpleNumericComparison[] = [firstCmp];
    let j = i + 1;
    while (j < statements.length) {
      const next = statements[j];
      if (next === undefined || next.kind !== 'IfStatement' || classifyIf(next) !== 'simple') break;
      const cmp = asSimpleNumericComparison(next.condition);
      if (cmp === null || !exprEquals(cmp.left, firstCmp.left)) break;
      if (assignedNames(next.thenBranch).has(varName)) break;
      const related = comparisons.every((c) => numericComparisonImplies(c, cmp) || numericComparisonsDisjoint(c, cmp));
      if (!related) break;
      chain.push(next);
      comparisons.push(cmp);
      j++;
    }

    if (chain.length >= 2) {
      chains.push({ statements: chain, apply: () => mergeNestedSimpleChain(chain, comparisons) });
      i = j;
    } else {
      i++;
    }
  }
  return chains;
}

// --- Ανίχνευση: τι μετατροπές ισχύουν για μια συγκεκριμένη δομή -------------

export interface SelectionConversionOption {
  readonly targetShape: SelectionShape;
  readonly apply: () => readonly Statement[];
}

/** Όλες οι έγκυρες μετατροπές-ΕΝΟΣ-κόμβου για ένα δοσμένο ΑΝ ή ΕΠΙΛΕΞΕ (χωρίς τη μετατροπή ζεύγους Απλή↔Σύνθετη — βλ. `findSimplePairs`). */
export function availableSingleNodeConversions(stmt: IfStatement | SwitchStatement): SelectionConversionOption[] {
  const options: SelectionConversionOption[] = [];

  // Προσθέτει "→ Απλή" ΜΟΝΟ όταν το σπάσιμο σε ανεξάρτητες Απλές ΑΝ δεν
  // αλλάζει συμπεριφορά — βλ. `toSimpleSequence`/`hasUnsafeMutationForSplit`.
  function pushSimple(node: IfStatement): void {
    const asSimple = toSimpleSequence(node);
    if (asSimple !== null) options.push({ targetShape: 'simple', apply: () => asSimple });
  }

  if (stmt.kind === 'SwitchStatement') {
    const asMultiple = switchToMultiple(stmt);
    if (asMultiple !== null) {
      options.push({ targetShape: 'multiple', apply: () => [asMultiple] });
      options.push({ targetShape: 'nested-else', apply: () => [multipleToNestedElse(asMultiple)] });
      pushSimple(asMultiple);
    }
    return options;
  }

  const shape = classifyIf(stmt);
  switch (shape) {
    case 'simple': {
      const nestedThen = simpleToNestedThen(stmt);
      if (nestedThen !== null) options.push({ targetShape: 'nested-then', apply: () => [nestedThen] });
      break;
    }
    case 'composite':
      pushSimple(stmt);
      break;
    case 'multiple': {
      const asSwitch = multipleToSwitch(stmt);
      if (asSwitch !== null) options.push({ targetShape: 'switch', apply: () => [asSwitch] });
      options.push({ targetShape: 'nested-else', apply: () => [multipleToNestedElse(stmt)] });
      pushSimple(stmt);
      break;
    }
    case 'nested-else': {
      const flattened = nestedElseToMultiple(stmt);
      options.push({ targetShape: 'multiple', apply: () => [flattened] });
      const asSwitch = multipleToSwitch(flattened);
      if (asSwitch !== null) options.push({ targetShape: 'switch', apply: () => [asSwitch] });
      pushSimple(flattened);
      break;
    }
    case 'nested-then':
      options.push({ targetShape: 'simple', apply: () => [nestedThenToSimple(stmt)] });
      break;
  }

  // "Αφαίρεση όλων των εμφωλευμένων ΑΝ" — γενικό ξεδίπλωμα ΟΠΟΙΟΥΔΗΠΟΤΕ
  // βάθους (βλ. `flattenNestedIf`), πέρα από το στενό nested-else/nested-then
  // μοτίβο πιο πάνω (που ήδη καλύπτεται εκεί με το ΙΔΙΟ ακριβώς αποτέλεσμα σε
  // εκείνη την ειδική περίπτωση — δεν χρειάζεται δεύτερο, πανομοιότυπο κουμπί).
  if (shape !== 'nested-else' && shape !== 'nested-then' && ifHasAnyNesting(stmt)) {
    const flattened = flattenNestedIf(stmt);
    if (flattened !== null) options.push({ targetShape: classifyIf(flattened), apply: () => [flattened] });
  }
  return options;
}
